import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  OverlayView,
  Marker,
} from "@react-google-maps/api";
import { Paper, Box } from "@mui/material";
import { DirectionsCache } from "../../components/dashboard/mapConstants";
import { getPlaceConfig } from "../../components/dashboard/functions.js";
import {
  dIcon,
  getBusIcon,
  getLeftToRightBusIcon,
  getRightToLeftBusIcon,
} from "./vehicleIcons";
import { getRightToLeftBusIcon1 } from "./RotationSvg";
import { calculateDistance } from "../../utils/calculateDistance";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import { computeVehicleLabelOffsets } from "../../utils/mapLabelOffsets.js";

const mapStyles = [
  { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const parseCoordinate = (value) =>
  typeof value === "string" ? parseFloat(value) : isNaN(value) ? null : value;

const createStopRouteCacheKey = (stopId, route, stops) => {
  const stopSignature = (stops || [])
    .map(
      (s) =>
        `${parseFloat(s.latitude).toFixed(6)}_${parseFloat(s.longitude).toFixed(
          6,
        )}`,
    )
    .join("-");
  return `${stopId}_${route.id}_stops_${stopSignature}`;
};

const MapComponent = ({
  mapCenter,
  onMapLoad,
  directions,
  filteredStops,
  highlightedStopId,
  onStopClick,
  vehicleData,
  labelPositions,
  allVehicles,
  routesForStop = [],
  vehicleDirections = {},
  allRoutes = [],
  vehicleStatuses = {},
}) => {
  const [stopDirections, setStopDirections] = useState([]);
  const [snappedPositions, setSnappedPositions] = useState({});
  const [placeMarkers, setPlaceMarkers] = useState([]);
  const [zoom, setZoom] = useState(16);
  const mapRef = useRef(null);
  const lastKnownHeadings = useRef({});

  const { settings } = useSettings();
  const { getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(settings, allVehicles, filteredStops, allRoutes);

  const mapDetailsArray = settings.mapDetails || [];

  // Fetch places using Places API
  useEffect(() => {
    if (!mapRef.current || !mapDetailsArray.length) {
      setPlaceMarkers([]);
      return;
    }

    const searchTimeout = setTimeout(() => {
      const mapCenter = mapRef.current?.getCenter();
      if (!mapCenter) return;

      const placesService = new window.google.maps.places.PlacesService(
        mapRef.current,
      );
      const placeTypes = mapDetailsArray.filter(
        (detail) => detail !== "STREET_NAMES",
      );

      setPlaceMarkers([]);
      if (placeTypes.length === 0) return;

      let centerLat, centerLng;
      try {
        centerLat = mapCenter.lat();
        centerLng = mapCenter.lng();
        if (isNaN(centerLat) || isNaN(centerLng)) return;
      } catch (e) {
        return;
      }

      const allPlacePromises = placeTypes.map(
        (placeType) =>
          new Promise((resolve) => {
            const placeConfig = getPlaceConfig(placeType);
            if (!placeConfig?.type) {
              resolve([]);
              return;
            }
            placesService.nearbySearch(
              {
                location: new window.google.maps.LatLng(centerLat, centerLng),
                radius: 20000,
                type: placeConfig.type,
              },
              (results, status) => {
                if (status === "OK" && results) {
                  resolve(
                    results.map((place) => ({
                      id: place.place_id,
                      name: place.name,
                      position: {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                      },
                      type: placeType,
                    })),
                  );
                } else {
                  resolve([]);
                }
              },
            );
          }),
      );

      Promise.all(allPlacePromises).then((allMarkers) => {
        setPlaceMarkers(allMarkers.flat());
      });
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [mapRef.current, mapDetailsArray]);

  const getMapOptions = useCallback(() => {
    const showStreetNames = mapDetailsArray.includes("STREET_NAMES");
    return {
      styles: [
        ...mapStyles,
        {
          featureType: "road",
          elementType: "labels.text",
          stylers: [{ visibility: showStreetNames ? "on" : "off" }],
        },
        {
          featureType: "water",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
      ],
      minZoom: 15.6,
      maxZoom: 20,
    };
  }, [mapDetailsArray]);

  const MAX_SNAP_DISTANCE = 50;
  const lastValidPositions = useRef({});
  const vehicleMovementState = useRef({});

  // console.log(allVehicles, "vehicleData")
  const deviceIdToName = useMemo(() => {
    const map = {};
    (allVehicles || []).forEach((v) => {
      map[v.device_id] = v.name;
    });
    return map;
  }, [allVehicles]);

  const deviceIdToStatus = useMemo(() => {
    const map = {};
    (allVehicles || []).forEach((v) => {
      map[v.device_id] = vehicleStatuses[v.device_id] || v.status;
    });
    return map;
  }, [allVehicles, vehicleStatuses]);

  const deviceIdToVehicles_color = useMemo(() => {
    const map = {};
    (allVehicles || []).forEach((v) => {
      map[v.device_id] = v.color || "green";
    });
    return map;
  }, [allVehicles]);

  // ─── Vehicle label collision offsets ──────────────────────────────────────────
  // Pixel-space vertical stacking so nearby vehicle labels don't overlap.
  const labelOffsets = useMemo(() => {
    const map = mapRef.current;
    if (!map) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = Object.entries(vehicleData || {}).map(
      ([deviceId, vehicle]) => ({
        deviceId,
        lat: parseFloat(vehicle?.position?.lat),
        lng: parseFloat(vehicle?.position?.lng),
      }),
    );
    return computeVehicleLabelOffsets(projection, zoom, entries, {
      BASE_OFFSET: 55,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleData, zoom]);

  const centerOnStop = useCallback(() => {
    if (!mapRef.current || !highlightedStopId) return;
    const stop = filteredStops.find(
      (s) => s.id.toString() === highlightedStopId.toString(),
    );
    if (stop) {
      const pos = {
        lat: parseCoordinate(stop.latitude),
        lng: parseCoordinate(stop.longitude),
      };
      mapRef.current.setCenter(pos);
      mapRef.current.setZoom(16);
    }
  }, [highlightedStopId, filteredStops]);

  useEffect(() => {
    centerOnStop();
  }, [centerOnStop]);

  useEffect(() => {
    if (stopDirections.length > 0 && highlightedStopId && mapRef.current) {
      const timer = setTimeout(() => {
        centerOnStop();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [stopDirections, centerOnStop, highlightedStopId]);

  useEffect(() => {
    if (!routesForStop?.length) {
      setStopDirections([]);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    Promise.all(
      routesForStop.map(async (route) => {
        const cacheKey = createStopRouteCacheKey(
          highlightedStopId || "all",
          route,
          filteredStops,
        );

        const cached = DirectionsCache.get(cacheKey);
        if (cached) {
          return {
            result: cached.result,
            color: cached.color || route.color || "#20A8E0",
            id: route.id,
          };
        }

        const coords = route.routeCoordinates || route.coordinates || [];
        if (coords.length < 2) {
          return null;
        }

        const origin = {
          lat: parseCoordinate(coords[0].lat || coords[0].latitude),
          lng: parseCoordinate(coords[0].lng || coords[0].longitude),
        };
        const destination = {
          lat: parseCoordinate(
            coords[coords.length - 1].lat || coords[coords.length - 1].latitude,
          ),
          lng: parseCoordinate(
            coords[coords.length - 1].lng ||
              coords[coords.length - 1].longitude,
          ),
        };
        const waypoints = coords.slice(1, -1).map((c) => ({
          location: {
            lat: parseCoordinate(c.lat || c.latitude),
            lng: parseCoordinate(c.lng || c.longitude),
          },
          stopover: false,
        }));

        return new Promise((resolve) => {
          directionsService.route(
            {
              origin,
              destination,
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
              optimizeWaypoints: false,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                DirectionsCache.set(cacheKey, {
                  result,
                  color: route.color || "#20A8E0",
                });
                resolve({
                  result,
                  color: route.color || "#20A8E0",
                  id: route.id,
                });
              } else {
                console.error(
                  `Directions request failed for route ${route.id}: ${status}`,
                );
                resolve(null);
              }
            },
          );
        });
      }),
    ).then((results) => {
      setStopDirections(results.filter(Boolean));
    });

    return () => {
      setStopDirections([]);
    };
  }, [routesForStop, highlightedStopId, filteredStops]);

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (onMapLoad) {
        onMapLoad(map);
      }
      if (highlightedStopId) {
        const stop = filteredStops.find(
          (s) => s.id.toString() === highlightedStopId.toString(),
        );
        if (stop && mapRef.current) {
          const pos = {
            lat: parseCoordinate(stop.latitude),
            lng: parseCoordinate(stop.longitude),
          };
          mapRef.current.setCenter(pos);
          mapRef.current.setZoom(16);
        }
      }
    },
    [onMapLoad, highlightedStopId, filteredStops],
  );

  const smoothHeading = (
    deviceId,
    currentHeading,
    newHeading,
    isMoving,
    threshold = 30,
  ) => {
    // If vehicle is not moving, keep the last known heading when it was moving
    if (!isMoving && lastKnownHeadings.current[deviceId]) {
      return lastKnownHeadings.current[deviceId];
    }

    // If moving and heading change is significant, update it
    if (isMoving && Math.abs(newHeading - currentHeading) > threshold) {
      return newHeading;
    }

    // Otherwise keep current heading
    return currentHeading;
  };

  const findNearestPointOnRoute = useCallback(
    (position, routeDirections) => {
      if (!position || !routeDirections?.routes?.[0]) return null;
      const route = routeDirections.routes[0];
      let closestPoint = null;
      let minDistance = Infinity;
      let routeIndex = -1;
      let currentRouteIndex = 0;

      route.legs.forEach((leg) => {
        leg.steps.forEach((step) => {
          const path = step.path;
          path.forEach((point, index) => {
            if (index === 0) return;
            const getLat = (p) =>
              typeof p.lat === "function" ? p.lat() : p.lat;
            const getLng = (p) =>
              typeof p.lng === "function" ? p.lng() : p.lng;

            const start = {
              lat: getLat(path[index - 1]),
              lng: getLng(path[index - 1]),
            };
            const end = {
              lat: getLat(point),
              lng: getLng(point),
            };

            const dx = end.lng - start.lng;
            const dy = end.lat - start.lat;
            const segmentLength = Math.sqrt(dx * dx + dy * dy);
            if (segmentLength === 0) return;

            const t =
              ((position.lng - start.lng) * dx +
                (position.lat - start.lat) * dy) /
              (segmentLength * segmentLength);

            if (t < 0) {
              const distToStart = calculateDistance(position, start);
              if (distToStart < minDistance) {
                minDistance = distToStart;
                closestPoint = start;
                routeIndex = currentRouteIndex;
              }
              return;
            }

            if (t > 1) {
              const distToEnd = calculateDistance(position, end);
              if (distToEnd < minDistance) {
                minDistance = distToEnd;
                closestPoint = end;
                routeIndex = currentRouteIndex + 1;
              }
              return;
            }

            const projectedPoint = {
              lat: start.lat + t * dy,
              lng: start.lng + t * dx,
            };
            const distance = calculateDistance(position, projectedPoint);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = projectedPoint;
              routeIndex = currentRouteIndex + t;
            }

            currentRouteIndex++;
          });
        });
      });

      // Only return snapped position if within acceptable distance
      if (minDistance <= MAX_SNAP_DISTANCE) {
        return {
          ...closestPoint,
          routeIndex: routeIndex,
          distanceFromRoute: minDistance,
        };
      }
      return null;
    },
    [calculateDistance, MAX_SNAP_DISTANCE],
  );

  // Update snapped positions when vehicle data changes
  useEffect(() => {
    const newSnappedPositions = {};

    Object.entries(vehicleData || {}).forEach(([deviceId, vehicle]) => {
      if (!vehicle?.position?.lat || !vehicle?.position?.lng) {
        return;
      }

      // Find the route assigned to this vehicle
      const assignedRoute = allRoutes.find((route) => {
        if (!route.routeCoordinates || !route.assignedVehicles) {
          return false;
        }
        return route.assignedVehicles.some(
          (assignedVehicle) => assignedVehicle.device_id === deviceId,
        );
      });

      // Find the corresponding directions for this route
      const routeDirections = stopDirections.find(
        (dir) => dir.id === assignedRoute?.id,
      )?.result;

      if (routeDirections) {
        const originalPosition = {
          lat: parseFloat(vehicle.position.lat),
          lng: parseFloat(vehicle.position.lng),
        };

        const snappedData = findNearestPointOnRoute(
          originalPosition,
          routeDirections,
        );

        if (snappedData) {
          newSnappedPositions[deviceId] = {
            ...snappedData,
            originalPosition,
            isSnapped: true,
          };
        } else {
          // Vehicle is off-route, use original position
          newSnappedPositions[deviceId] = {
            lat: originalPosition.lat,
            lng: originalPosition.lng,
            originalPosition,
            isSnapped: false,
            distanceFromRoute: Infinity,
          };
        }
      } else {
        // No route directions available, use original position
        const originalPosition = {
          lat: parseFloat(vehicle.position.lat),
          lng: parseFloat(vehicle.position.lng),
        };
        newSnappedPositions[deviceId] = {
          lat: originalPosition.lat,
          lng: originalPosition.lng,
          originalPosition,
          isSnapped: false,
          distanceFromRoute: null,
        };
      }
    });

    setSnappedPositions(newSnappedPositions);
  }, [vehicleData, stopDirections, allRoutes, findNearestPointOnRoute]);

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    return direction === "source_to_dest" ? " ➡️" : " ⬅️";
  };

  const getVehicleIcon = (deviceId, heading) => {
    const color = deviceIdToVehicles_color[deviceId] || "green";
    const rotatedSVG = getRightToLeftBusIcon(color, heading + 90).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(45, 45),
      anchor: new window.google.maps.Point(22.5, 22.5),
    };
  };

  return (
    <Paper
      sx={{
        height: { xs: "300px", sm: "400px", md: "90vh" },
        position: "relative",
        borderRadius: "20px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          zIndex: 1,
          top: 12,
          left: 10,
          background: "#FFFFFF",
          borderRadius: "5px",
          padding: "4px 8px",
          color: "#EE3B2B",
          fontFamily: "Roboto",
          fontWeight: 500,
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          "@keyframes pulse": {
            "0%": { transform: "scale(1)", opacity: 1 },
            "50%": { transform: "scale(1.4)", opacity: 0.6 },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      >
        <svg
          width="10"
          height="11"
          viewBox="0 0 10 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            animation: "pulse 1.5s infinite",
            transformOrigin: "center center",
          }}
        >
          <circle cx="5" cy="5.5" r="5" fill="#EE3B2B" />
        </svg>
        Live Map
      </Box>

      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "100%",
          border: "1px solid #000000",
          borderRadius: "20px",
        }}
        center={mapCenter}
        zoom={16}
        options={getMapOptions()}
        onLoad={handleMapLoad}
        onZoomChanged={() => {
          if (mapRef.current) setZoom(mapRef.current.getZoom());
        }}
      >
        {placeMarkers.map((place) => {
          const placeConfig = getPlaceConfig(place.type);
          if (!placeConfig?.icon) return null;
          return (
            <Marker
              key={place.id}
              position={place.position}
              title={place.name}
              icon={{
                url: placeConfig.icon,
                scaledSize: new window.google.maps.Size(32, 32),
              }}
            />
          );
        })}
        {stopDirections.map((dir) => (
          <DirectionsRenderer
            key={dir.id}
            directions={dir.result}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: dir.color,
                strokeOpacity: 0.8,
                strokeWeight: 5,
                zIndex: 1,
              },
            }}
          />
        ))}

        {directions?.map((direction, i) => (
          <DirectionsRenderer
            key={`input-dir-${i}`}
            directions={direction}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#000",
                strokeOpacity: 0.5,
                strokeWeight: 4,
                zIndex: 0,
              },
            }}
          />
        ))}

        {filteredStops.map((stop, index) => {
          const isHighlighted =
            highlightedStopId &&
            stop.id.toString() === highlightedStopId.toString();
          return (
            <React.Fragment key={stop.id || `stop-${index}`}>
              <OverlayView
                position={{
                  lat: parseCoordinate(stop.latitude),
                  lng: parseCoordinate(stop.longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{ position: "relative", top: "-69px", left: "0px" }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: isHighlighted ? "#FFD700" : "white",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      color: "black",
                      fontWeight: isHighlighted ? 600 : 400,
                      fontSize: isHighlighted ? "16px" : "12px",
                      boxShadow: isHighlighted
                        ? "0 2px 8px rgba(0,0,0,0.4)"
                        : "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: isHighlighted ? 1001 : 1000,
                      border: isHighlighted ? "2px solid #FF4500" : "none",
                    }}
                  >
                    {stop.name}
                  </div>
                </div>
              </OverlayView>
              <Marker
                position={{
                  lat: parseCoordinate(stop.latitude),
                  lng: parseCoordinate(stop.longitude),
                }}
                title={stop.name || `Stop ${index + 1}`}
                icon={
                  isHighlighted
                    ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: "#FFD700",
                        fillOpacity: 1,
                        strokeColor: "#FF4500",
                        strokeWeight: 3,
                      }
                    : getIntermediateStopIcon() || undefined
                }
                onClick={() => onStopClick(stop)}
              />
            </React.Fragment>
          );
        })}

        {Object.entries(vehicleData || {}).map(([deviceId, vehicle]) => {
          if (!vehicle?.position?.lat || !vehicle?.position?.lng) {
            return null;
          }

          const currentPos = {
            lat: parseFloat(vehicle.position.lat),
            lng: parseFloat(vehicle.position.lng),
          };

          // Check if vehicle is moving by comparing with last position
          const lastPos = lastValidPositions.current[deviceId];
          const isMoving = lastPos
            ? Math.abs(currentPos.lat - lastPos.lat) > 0.00001 ||
              Math.abs(currentPos.lng - lastPos.lng) > 0.00001
            : true;

          // Update position tracking
          lastValidPositions.current[deviceId] = currentPos;
          vehicleMovementState.current[deviceId] = isMoving;

          // Get the snapped position for this vehicle
          const snappedData = snappedPositions[deviceId];

          // Use snapped position if available, otherwise use original position
          const displayPosition = snappedData?.isSnapped
            ? {
                lat: snappedData.lat,
                lng: snappedData.lng,
              }
            : {
                lat: parseFloat(vehicle.position.lat),
                lng: parseFloat(vehicle.position.lng),
              };

          // IMPORTANT: Use the heading from parent (vehicle.heading) - don't recalculate
          // The parent has already calculated the proper heading based on movement
          const vehicleHeading = vehicle.heading;

          // Initialize heading for this device if not exists
          if (!lastKnownHeadings.current[deviceId]) {
            lastKnownHeadings.current[deviceId] = vehicleHeading || 0;
          }

          // Only update heading if we have a valid new heading value
          // This prevents the "rocket" issue when data stops coming
          let smoothedHeading = lastKnownHeadings.current[deviceId];

          if (
            vehicleHeading !== undefined &&
            vehicleHeading !== null &&
            !isNaN(vehicleHeading)
          ) {
            // Apply smooth heading transition only with valid heading
            smoothedHeading = smoothHeading(
              lastKnownHeadings.current[deviceId],
              vehicleHeading,
              30,
            );
            lastKnownHeadings.current[deviceId] = smoothedHeading;
          }
          // If vehicleHeading is invalid, keep the last known heading
          const OVERSPEED_THRESHOLD = settings?.maxSpeed || 30;
          const currentSpeed = parseFloat(vehicle.speed);
          const isOverSpeed = currentSpeed > OVERSPEED_THRESHOLD;
          return (
            <React.Fragment key={deviceId}>
              <OverlayView
                position={displayPosition}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    position: "relative",
                    top: "-20px",
                    left: "0px",
                    width: "2px",
                    height: "2px",
                  }}
                >
                  {(() => {
                    const topPx = labelOffsets[deviceId]?.topPx ?? 55;
                    // connector from just below icon bottom (~35px) to the label top
                    const connectorH = topPx - 35;
                    return (
                      <>
                        {connectorH > 0 && (
                          <svg
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              top: "35px",
                              pointerEvents: "none",
                              overflow: "visible",
                              zIndex: 999,
                            }}
                            width="2"
                            height={connectorH}
                          >
                            <line
                              x1="1"
                              y1="0"
                              x2="1"
                              y2={connectorH}
                              stroke="black"
                              strokeWidth="1.8"
                              strokeDasharray="3 2"
                            />
                          </svg>
                        )}
                        <div
                          style={{
                            position: "absolute",
                            top: labelOffsets[deviceId]?.top ?? "10px",
                            left: labelOffsets[deviceId]?.left ?? "50%",
                            transform:
                              labelOffsets[deviceId]?.transform ??
                              "translateX(-50%)",
                            color:
                              deviceIdToStatus[deviceId] === "INACTIVE" ||
                              deviceIdToStatus[deviceId] === "DEAD"
                                ? "black"
                                : "white",
                            fontSize: "10px",
                            fontWeight: 600,
                            fontFamily: "Arial, sans-serif",
                            lineHeight: "14.06px",
                            padding: "8px",
                            whiteSpace: "nowrap",
                            backgroundColor:
                              deviceIdToStatus[deviceId] === "DEAD"
                                ? "#F9E719"
                                : deviceIdToStatus[deviceId] === "INACTIVE"
                                  ? "#F9E719"
                                  : "#008C23",
                            borderRadius: "12px",
                            zIndex: 1000,
                            border: "1px solid black",
                          }}
                        >
                          {deviceIdToName[deviceId] || vehicle.name || deviceId}
                          <span style={{ fontWeight: "bold" }}>
                            {getDirectionArrow(deviceId)}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </OverlayView>
              <Marker
                position={displayPosition}
                title={`Vehicle ${
                  deviceIdToName[deviceId] || vehicle.name || deviceId
                }`}
                icon={getCustomVehicleIcon(
                  deviceId,
                  smoothedHeading,
                  vehicleDirections[deviceId],
                )}
                zIndex={1000}
              />
              {isOverSpeed && (
                <OverlayView
                  position={displayPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      position: "relative",
                      top: "-50px",
                      left: "25px",
                    }}
                  >
                    <div
                      title={`OVERSPEED: ${currentSpeed} km/h`}
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: "#FF0000",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #FFFFFF",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                        animation: "pulse 1.5s infinite",
                        zIndex: 1001,
                      }}
                    >
                      <span
                        style={{
                          color: "#FFFFFF",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        ⚠
                      </span>
                    </div>
                  </div>
                </OverlayView>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </Paper>
  );
};

export default MapComponent;
